import { useTranslation } from 'next-i18next';
import { FC, useContext, useMemo, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Box, Tooltip, Button } from '@mui/material';
import { CheckOutlined, InfoOutlined } from '@mui/icons-material';
import Collapse from '@mui/material/Collapse';

import { BillingCycle, PlanFeatureLevelSupport } from '@/features/common/pricing';
import { Container } from '@/components';

import styles from './styles.module.scss';
import { PlanFeaturesMetadataContext, PricingPageContext } from '../../contexts';

const PlanFeatureLabelTableCell: FC<{
  title: string;
  hint: string;
}> = ({ title, hint }) => {
  return (
    <TableCell component="th" scope="row">
      <span className={styles.tableCellValue}>{title}</span>
      <Tooltip title={hint}>
        <Box className={styles.tableCellTooltip}>
          <InfoOutlined />
        </Box>
      </Tooltip>
    </TableCell>
  );
};

const PlanFeatureValueTableCell: FC<{
  feature?: PlanFeatureLevelSupport;
  billingCycle: BillingCycle.Monthly | BillingCycle.Yearly;
}> = ({ feature, billingCycle }) => {
  const { t } = useTranslation('pricingV2');

  if (!feature)
    return (
      <TableCell align="center" className={styles.tableCell}>
        {null}
      </TableCell>
    );

  if (feature.brandTag === 'unsupported')
    return (
      <TableCell align="center" className={styles.tableCell}>
        {null}
      </TableCell>
    );

  if (feature.brandTag === 'supported') {
    return (
      <TableCell align="center" className={styles.tableCell}>
        <Box className={styles.tableCellIcon}>
          <CheckOutlined />
        </Box>
      </TableCell>
    );
  }

  if (feature.brandTag === 'limited') {
    return (
      <TableCell align="center" className={styles.tableCell}>
        {feature.translationKey
          ? t(feature.translationKey, { limit: feature.limit })
          : feature.limit}
      </TableCell>
    );
  }

  if (feature.brandTag === 'unlimited') {
    return (
      <TableCell align="center" className={styles.tableCell}>
        {t(feature.translationKey)}
      </TableCell>
    );
  }

  if (feature.brandTag === 'comingSoon') {
    return (
      <TableCell align="center" className={styles.tableCell}>
        {t(feature.translationKey)}
      </TableCell>
    );
  }

  if (feature.brandTag === 'billingCycleDependent') {
    const subFeature = feature.support[billingCycle];

    return <PlanFeatureValueTableCell feature={subFeature} billingCycle={billingCycle} />;
  }

  return null;
};

export const ComparePlansTable: FC = () => {
  const { t } = useTranslation('pricingV2', { keyPrefix: 'comparePlans' });

  const [isExpanded, setIsExpanded] = useState(false);

  const { productsMetadata, billingCycle } = useContext(PricingPageContext);
  const planFeaturesMetadata = useContext(PlanFeaturesMetadataContext);

  const planTypes = useMemo(
    () => productsMetadata.map((p) => p.family),
    [productsMetadata],
  );

  const plansHeaderCells = useMemo(
    () =>
      productsMetadata.map((metadata) => (
        <TableCell className={styles.tableHeader} align="center" key={metadata.family}>
          {metadata.name}
        </TableCell>
      )),
    [productsMetadata],
  );

  return (
    <Box className={styles.wrapper}>
      <Container>
        <Collapse in={isExpanded}>
          {Object.entries(planFeaturesMetadata).map(([category, metadata]) => {
            const title = metadata.title;

            return (
              <TableContainer
                key={category}
                component={Paper}
                className={styles.tableContainer}
              >
                <Table className={styles.table}>
                  <TableHead>
                    <TableRow>
                      <TableCell className={styles.tableHeader}>{title}</TableCell>
                      {plansHeaderCells}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {metadata.features.map((feature) => (
                      <TableRow className={styles.tableRow} key={feature.title}>
                        <PlanFeatureLabelTableCell
                          title={feature.title}
                          hint={feature.hint}
                        />
                        {planTypes.map((planType) => {
                          return (
                            <PlanFeatureValueTableCell
                              key={planType}
                              feature={feature.supportByPlan[planType]}
                              billingCycle={billingCycle}
                            />
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            );
          })}
        </Collapse>
        <Box className={styles.actions}>
          <Button
            color="info"
            variant="outlined"
            onClick={() => setIsExpanded((prev) => !prev)}
          >
            {isExpanded ? t('seeLess') : t('seeMore')}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};
